/**
 * The success envelope the documents share: `{ success, code, message, payload }`.
 * A body that is not that shape is a contract error. The screen prints `message`.
 */
import { z } from "zod/mini";
import { AppError } from "./problems";

const baseResponseSchema = z.object({
  success: z.optional(z.boolean()),
  code: z.optional(z.string()),
  message: z.optional(z.string()),
});

export function payloadOf<T>(schema: z.ZodMiniType<T>, body: unknown, path: string, status: number): T {
  const payload =
    body && typeof body === "object" && "payload" in body
      ? (body as { payload?: unknown }).payload
      : undefined;
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new AppError("contract", {
      status,
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
  return parsed.data;
}

// A negative count cannot describe a page; OpenAPI has no minimum yet
// (Patients GAPS B6, Settings GAPS G). Preserve this existing parser rule.
const pageCount = z.optional(z.int().check(z.gte(0)));

/**
 * A paged read. `totalElements` and `totalPages` sit beside `payload`.
 * Either count may be absent; an absent count is not turned into `0`.
 * A count that is not a whole number of zero or more is a contract error.
 */
export function pageOf<T>(
  schema: z.ZodMiniType<T>,
  body: unknown,
  path: string,
  status: number,
): { payload: T; totalElements: number | undefined; totalPages: number | undefined } {
  const parsed = z.object({ totalElements: pageCount, totalPages: pageCount }).safeParse(body);
  if (!parsed.success) {
    throw new AppError("contract", {
      status,
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
  return {
    payload: payloadOf(schema, body, path, status),
    totalElements: parsed.data.totalElements,
    totalPages: parsed.data.totalPages,
  };
}

/** Update and delete answer `BaseResponse` and no record. */
export function acceptBaseResponse(body: unknown, path: string, status: number): void {
  const parsed = baseResponseSchema.safeParse(body);
  if (!parsed.success) {
    throw new AppError("contract", {
      status,
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
}
